import { useState } from "react";
import { BookOpen, Download, Printer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";

const esc = (s) => s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
const inline = (s) => esc(s).replace(/\*\*(.+?)\*\*/g, "<b>$1</b>").replace(/`([^`]+)`/g, "<code>$1</code>");

// Basit Markdown → HTML (başlık, tablo, liste, paragraf)
export function mdToHtml(md) {
  const out = [];
  const lines = md.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (/^\|/.test(l)) {
      const rows = [];
      while (i < lines.length && /^\|/.test(lines[i])) rows.push(lines[i++]);
      i--;
      const cells = (r) => r.replace(/^\||\|$/g, "").split("|").map((c) => inline(c.trim()));
      const body = rows.filter((r) => !/^\|\s*:?-{2,}/.test(r));
      out.push(`<table><thead><tr>${cells(body[0]).map((c) => `<th>${c}</th>`).join("")}</tr></thead><tbody>${body.slice(1).map((r) => `<tr>${cells(r).map((c) => `<td>${c}</td>`).join("")}</tr>`).join("")}</tbody></table>`);
    } else if (/^#{1,4} /.test(l)) {
      const n = l.match(/^#+/)[0].length;
      out.push(`<h${n}>${inline(l.slice(n + 1))}</h${n}>`);
    } else if (/^\s*[-*] /.test(l)) {
      const items = [];
      while (i < lines.length && /^\s*[-*] /.test(lines[i])) items.push(`<li>${inline(lines[i++].replace(/^\s*[-*] /, ""))}</li>`);
      i--;
      out.push(`<ul>${items.join("")}</ul>`);
    } else if (/^\d+\. /.test(l)) {
      const items = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) items.push(`<li>${inline(lines[i++].replace(/^\d+\. /, ""))}</li>`);
      i--;
      out.push(`<ol>${items.join("")}</ol>`);
    } else if (l.trim()) out.push(`<p>${inline(l)}</p>`);
  }
  return out.join("\n");
}

const CSS = "body{font-family:Georgia,serif;margin:28px;color:#111;font-size:13px;line-height:1.5}h1{font-size:22px}h2{font-size:17px;border-bottom:1px solid #ccc;margin-top:26px}h3{font-size:14px}table{border-collapse:collapse;width:100%;margin:8px 0}th,td{border:1px solid #bbb;padding:4px 6px;text-align:left;vertical-align:top}th{background:#eee}code{background:#f3f3f3;padding:0 3px}";

export function UsageReportCard() {
  const [md, setMd] = useState(null);
  const [open, setOpen] = useState(false);
  const load = async () => {
    try { const { data } = await api.get("/system/usage-report"); setMd(data.markdown); setOpen(true); } catch (e) { toast.error(apiError(e)); }
  };
  const print = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>Kullanım ve Test Raporu</title><style>${CSS}</style></head><body>${mdToHtml(md)}</body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([md], { type: "text/markdown;charset=utf-8" }));
    const a = document.createElement("a"); a.href = url; a.download = "KULLANIM_RAPORU.md"; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <div className="panel p-4" data-testid="usage-report-card">
      <div className="flex items-center justify-between gap-2">
        <div><div className="font-semibold flex items-center gap-2"><BookOpen className="h-4 w-4" />Kullanım Raporu</div>
          <p className="text-[12px] text-slate-500">Her modülün ne yaptığı, nasıl kullanıldığı, test sonuçları ve bilinen sınırlar.</p></div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={open ? () => setOpen(false) : load} data-testid="usage-report-toggle">{open ? "Gizle" : "Görüntüle"}</Button>
          {md && <Button size="sm" variant="outline" onClick={print} data-testid="usage-report-print"><Printer className="h-4 w-4 mr-1" />Yazdır / PDF</Button>}
          {md && <Button size="sm" variant="outline" onClick={download} data-testid="usage-report-download"><Download className="h-4 w-4 mr-1" />.md indir</Button>}
        </div>
      </div>
      {open && md && <div className="usage-report mt-3 max-h-[70vh] overflow-auto border rounded p-4 text-[13px]" data-testid="usage-report-content" dangerouslySetInnerHTML={{ __html: mdToHtml(md) }} />}
    </div>
  );
}
